import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Spinner,
  Table,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import PageHeader from "../components/shared/PageHeader";
import ImagePreviewModal from "../components/shared/ImagePreviewModal";
import PageContainer from "../components/shared/PageContainer";
import { formatDate, formatDateTime } from "../utils/formatters";
import { ordersApi } from "../services/services";
import { Order } from "../types";
import { useOrdersTableRefresh } from "../hooks/useOrdersTableRefresh";
import TableControls from "../components/shared/TableControls";

const STATUS_OPTIONS = [
  { key: "Pending", text: "Pending", value: "Pending" },
  { key: "InProgress", text: "In Progress", value: "In Progress" },
  { key: "Completed", text: "Completed", value: "Completed" },
];

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;

const toDateInputValue = (value?: string | Date | null): string => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};

const getStatusClasses = (status: string): string => {
  const normalized = status.toLowerCase();

  if (normalized.includes("complete")) {
    return "bg-success-subtle text-success-emphasis border-success-subtle";
  }

  if (normalized.includes("progress")) {
    return "bg-warning-subtle text-warning-emphasis border-warning-subtle";
  }

  return "bg-light text-body-secondary border-light";
};

const getFirstImage = (order: Order): string | null => {
  if (!order.formData) return null;
  try {
    const parsed = JSON.parse(order.formData);
    for (const key of ["picture1", "picture2", "image"]) {
      if (parsed[key] && typeof parsed[key] === "string" && parsed[key].startsWith("data:image")) {
        return parsed[key];
      }
    }
  } catch {}
  return null;
};

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [dateOrdered, setDateOrdered] = useState("");
  const [dateRequired, setDateRequired] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchOrders = useCallback(async () => {
    return await ordersApi.getOrders();
  }, []);

  const { refreshOrders, isRefreshing } = useOrdersTableRefresh<Order>(
    fetchOrders,
    setOrders,
    setLoading,
    (error) => console.error("Failed to fetch orders:", error)
  );

  // Initial fetch + polling for updates (replaces SignalR)
  useEffect(() => {
    refreshOrders({ showLoading: true });

    // Poll for new orders periodically
    const interval = setInterval(() => {
      refreshOrders();
    }, POLLING_INTERVAL);

    const onSupplierChange = () => refreshOrders({ showLoading: true });
    window.addEventListener("mockSupplierChanged", onSupplierChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("mockSupplierChanged", onSupplierChange);
    };
  }, [refreshOrders]);

  const handleStatusChange = (order: Order, newStatus: string) => {
    // Use functional update to avoid stale closure issues when changing multiple statuses
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o))
    );

    ordersApi.updateOrderStatus(order.id, newStatus).catch((error) => {
      console.error("Error updating status:", error);
      // Revert by refetching from server on failure
      refreshOrders();
    });
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      !searchTerm ||
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.clientId?.toString().includes(searchTerm);

    let matchesDateOrdered = true;
    if (dateOrdered && order.dateOrdered) {
      matchesDateOrdered = toDateInputValue(order.dateOrdered) === dateOrdered;
    }

    let matchesDateRequired = true;
    if (dateRequired && order.dateRequired) {
      matchesDateRequired = toDateInputValue(order.dateRequired) === dateRequired;
    }

    return matchesSearch && matchesDateOrdered && matchesDateRequired;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateOrdered, dateRequired, entriesPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / entriesPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * entriesPerPage;
  const displayedOrders = filteredOrders.slice(startIndex, startIndex + entriesPerPage);
  const startEntry = filteredOrders.length === 0 ? 0 : startIndex + 1;
  const endEntry =
    filteredOrders.length === 0
      ? 0
      : Math.min(startIndex + displayedOrders.length, filteredOrders.length);

  return (
    <PageContainer>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <PageHeader title="Manage Orders" subTitle="Orders" />

          <div className="ms-lg-auto">
            <Button
              variant="light"
              onClick={() => refreshOrders()}
              disabled={isRefreshing}
              className="border rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
            >
              {isRefreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </>
              )}
            </Button>
          </div>

        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-lg-4">
            <Row className="g-3 mb-3">
              <Col xs={12} md={6} xl={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar-event"></i>
                  </InputGroup.Text>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-date-ordered">Date Ordered</Tooltip>}
                  >
                    <Form.Control
                      type="date"
                      value={dateOrdered}
                      onChange={(e) => setDateOrdered(e.target.value)}
                      className="border-start-0"
                      aria-label="Date ordered"
                    />
                  </OverlayTrigger>
                </InputGroup>
              </Col>
              <Col xs={12} md={6} xl={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar2-check"></i>
                  </InputGroup.Text>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-date-required">Date Required</Tooltip>}
                  >
                    <Form.Control
                      type="date"
                      value={dateRequired}
                      onChange={(e) => setDateRequired(e.target.value)}
                      className="border-start-0"
                      aria-label="Date required"
                    />
                  </OverlayTrigger>
                </InputGroup>
              </Col>
            </Row>

            <TableControls
              entriesPerPage={entriesPerPage}
              onEntriesChange={setEntriesPerPage}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search orders..."
              showSearchIcon
            />

            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="small text-uppercase text-secondary">
                  <tr>
                    <th className="border-bottom pb-3 pt-2">Order</th>
                    <th className="border-bottom pb-3 pt-2">Image</th>
                    <th className="border-bottom pb-3 pt-2">Branch</th>
                    <th className="border-bottom pb-3 pt-2">Date Ordered</th>
                    <th className="border-bottom pb-3 pt-2">Date Required</th>
                    <th className="border-bottom pb-3 pt-2">Status</th>
                    <th className="border-bottom pb-3 pt-2 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading orders...
                      </td>
                    </tr>
                  ) : displayedOrders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        No orders found.
                      </td>
                    </tr>
                  ) : (
                    displayedOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="py-4 fw-semibold text-dark">
                          {order.orderNumber || "-"}
                        </td>
                        <td className="py-4">
                          {getFirstImage(order) ? (
                            <a
                              href="#"
                              className="text-primary text-decoration-none small"
                              onClick={(e) => {
                                e.preventDefault();
                                setPreviewImage(getFirstImage(order));
                              }}
                            >
                              <i className="bi bi-image me-1"></i>View
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="py-4">{order.clientName || "-"}</td>
                        <td className="py-4 text-body-secondary">
                          {formatDateTime(order.dateOrdered)}
                        </td>
                        <td className="py-4 text-body-secondary">
                          {formatDate(order.dateRequired)}
                        </td>
                        <td className="py-4">
                          <Form.Select
                            size="sm"
                            value={order.status}
                            onChange={(e) => handleStatusChange(order, e.target.value)}
                            className={`w-auto rounded-pill fw-semibold border ${getStatusClasses(
                              order.status || ""
                            )}`}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.key} value={opt.value}>
                                {opt.text}
                              </option>
                            ))}
                          </Form.Select>
                          {(order as any).isEdited && (
                            <span
                              className="badge rounded-pill bg-info-subtle text-info-emphasis border-info-subtle border px-2 py-1 ms-1"
                              title="This order has been edited by the client"
                            >
                              <i className="bi bi-pencil-fill me-1"></i>
                              Edited
                            </span>
                          )}
                        </td>
                        <td className="py-4 text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            {order.status === "Pending" && (
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => navigate(`/orders/view/${order.id}?edit=true`)}
                                className="border rounded-pill px-3 shadow-sm"
                              >
                                <i className="bi bi-pencil-square me-1"></i>
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => navigate(`/orders/view/${order.id}`)}
                              className="border rounded-pill px-3 shadow-sm"
                            >
                              <i className="bi bi-eye me-1"></i>
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border-top mt-3 pt-3">
              <div className="small text-muted">
                Showing rows {startEntry} to {endEntry} of {filteredOrders.length}
              </div>

              <Pagination size="sm" className="mb-0 ms-md-auto">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === safeCurrentPage}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
              </Pagination>
            </div>
          </Card.Body>
        </Card>
      </div>
      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </PageContainer>
  );
};

export default Orders;
